import { useEffect, useState, useMemo } from "react";
import PageHeader from "../components/PageHeader";
import TimeFilter from "../components/TimeFilter";
import MonthRangePicker from "../components/MonthRangePicker";
import StatCard from "../components/StatCard";
import DataTable from "../components/DataTable";
import DashboardSection from "../components/DashboardSection";
import ErrorState from "../components/ErrorState";
import { SkeletonStatGrid, SkeletonTable } from "../components/Skeleton";
import { AlertIcon, ShieldIcon, CheckCircleIcon } from "../components/icons";

import {
  dpdToday,
  dpdYesterday,
  dpdLast7,
  dpdLast30,
  dpdRange
} from "../api/dpd.api";

export default function DPD() {
  const [filter, setFilter] = useState("today");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (filter !== "custom") loadData();
  }, [filter]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      let res;
      if (filter === "today") res = await dpdToday();
      else if (filter === "yesterday") res = await dpdYesterday();
      else if (filter === "last7") res = await dpdLast7();
      else if (filter === "last30") res = await dpdLast30();
      setData(res.data);
    } catch (err) {
      setError(err.message || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  const loadCustom = async ({ fromYear, fromMonth, toYear, toMonth }) => {
    setLoading(true);
    setError(null);
    try {
      const start = `${fromYear}-${String(fromMonth).padStart(2, "0")}-01`;
      const end = `${toYear}-${String(toMonth).padStart(2, "0")}-28`;
      const res = await dpdRange(start, end);
      setData(res.data);
    } catch (err) {
      setError(err.message || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  const dpdCounts = useMemo(() => {
    if (!data || !data.DPD_counts || typeof data.DPD_counts !== "object") return {};
    return data.DPD_counts;
  }, [data]);

  const totalLoans = data?.total_loans || 0;
  const zeroDpd = dpdCounts["0"] || 0;
  const zeroDpdPct = totalLoans > 0 ? Math.round((zeroDpd / totalLoans) * 100) : 0;
  const overdueLoans = Math.max(0, totalLoans - zeroDpd);

  const tableData = useMemo(() => {
    return Object.entries(dpdCounts)
      .map(([k, v]) => ({
        DPD: `Day ${k}`,
        count: typeof v === "number" ? v : 0,
        share: totalLoans > 0 ? `${((v / totalLoans) * 100).toFixed(1)}%` : "0%"
      }))
      .sort((a, b) => parseInt(a.DPD.replace(/\D/g, "")) - parseInt(b.DPD.replace(/\D/g, "")));
  }, [dpdCounts, totalLoans]);

  return (
    <div className="page-container">
      <PageHeader
        title="DPD Risk Dashboard"
        subtitle="Days-past-due distribution and portfolio health across the active loan book."
      />

      <div className="toolbar">
        <TimeFilter value={filter} onChange={setFilter} />
      </div>
      <MonthRangePicker open={filter === "custom"} onApply={loadCustom} />

      {error ? (
        <ErrorState message={error} onRetry={filter === "custom" ? undefined : loadData} />
      ) : (
        <>
          <DashboardSection title="Portfolio Health">
            {loading || !data ? (
              <SkeletonStatGrid count={3} />
            ) : (
              <div className="grid">
                <StatCard
                  title="Total Loans"
                  value={totalLoans}
                  icon={ShieldIcon}
                  index={0}
                  tone="accent"
                />
                <StatCard
                  title="DPD 0 (On-Time)"
                  value={zeroDpd}
                  subtext={`${zeroDpdPct}% of active portfolio`}
                  icon={CheckCircleIcon}
                  index={1}
                  tone="success"
                />
                <StatCard
                  title="Overdue (DPD > 0)"
                  value={overdueLoans}
                  subtext={totalLoans > 0 ? `${(100 - zeroDpdPct)}% overdue share` : "0%"}
                  icon={AlertIcon}
                  index={2}
                  tone="warn"
                />
              </div>
            )}
          </DashboardSection>

          <DashboardSection title="DPD Day Breakdown">
            {loading || !data ? (
              <SkeletonTable rows={5} />
            ) : (
              <DataTable
                columns={["DPD", "count", "share"]}
                data={tableData}
              />
            )}
          </DashboardSection>
        </>
      )}
    </div>
  );
}
